// "My inventions" — the Overlap view (Story #5648 build v2, figure S4b
// documentation/…-assets/screen-S4b-my-inventions-overlap.png).
//
// The figure's whole screen: a left rail of groups by band, with the weak-signal
// card, the duplicate-funding card and the "groups form at 40" footer under it,
// and on the right one open group — its lifecycle, its members, its evidence,
// its shared-ground mini-map and the fund-once workbench.
//
// WHAT MAKES THIS DIFFERENT FROM components/whitespace/OverlapView.tsx: at own
// scope the group TITLES are deterministic. They are read off the score band
// with overlapRelationshipHeadline/overlapTierForScore — the same thresholds
// the aggregator view uses, never a new one. AI-generated names and notes need
// an organisation key, and this screen says what the backend itself says about
// that rather than leaving a blank where a name would be.
//
// DATA — self-contained, so the host page only has to mount it:
//   GET  /whitespace/portfolio_overlap?scope=own          (groups + weak pairs)
//   GET  /whitespace/portfolio_overlap/funding?scope=own  (the fund-once rollup)
//   GET  /whitespace/portfolio_overlap/group/{id}/note?scope=own  (why naming
//        and notes are unavailable, in the backend's own words)
//   GET  /whitespace/portfolio_map?scope=own              (the mini-map layout)
//   POST /whitespace/portfolio_overlap/compute            ("↻ Re-run")
//   POST /whitespace/portfolio_overlap/group/{id}/status  (lifecycle)
//   POST /whitespace/portfolio_overlap/group/{id}/brief   (the .docx)
//
// It deliberately does NOT fall back to the aggregator-scoped fetcher when the
// own-scope call fails. Showing a zero-membership inventor a payload resolved
// from an aggregator context is a worse outcome than an honest empty screen.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileDown, Loader2, RefreshCw, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  getPortfolioOverlap,
  getPortfolioOverlapFunding,
  getPortfolioMap,
  getOverlapGroupNote,
  getOverlapGroupBrief,
  getDisclosureAgreements,
  computePortfolioOverlap,
  setOverlapGroupStatus,
} from '../../services/api'
import {
  overlapTierForScore,
  overlapRelationshipHeadline,
  projectTagColor,
  OVERLAP_STALE_GROUP_MESSAGE,
} from '../../lib/whitespace'
import { downloadBase64File } from '../whitespace/PortfolioWatchPanel'
import { getProjectStagePill } from '../../utils/projectStage'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import OwnOverlapMiniMap from './OwnOverlapMiniMap'
import OwnFundingCard from './OwnFundingCard'
import OwnNotifyBar from './OwnNotifyBar'
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'
import { comparedCoverageLine, ownGroupMetaLine } from './ownOverlapText'
import type { Project } from '../../types'
import type { OverlapGroup, OverlapGroupStatusTarget } from '../../types/whitespace'

/** overlap_lifecycle.py's VALID_STATUSES, in the figure's order. 'new' is not a
 * POST target — there is no "revert to new" affordance — so it renders as the
 * current state only. */
const LIFECYCLE_STEPS: OverlapGroupStatusTarget[] = ['reviewing', 'consolidating', 'consolidated', 'dismissed']
const LIFECYCLE_LABEL: Record<OverlapGroupStatusTarget, string> = {
  reviewing: 'Reviewing',
  consolidating: 'Consolidating',
  consolidated: 'Consolidated',
  dismissed: 'Dismiss',
}

/** The three bands the figure names, in its order. */
const BAND_SAME_WORK = 'Same work'
const BAND_WORTH_A_LOOK = 'Worth a look'
const BAND_WEAK = 'Weak signals'

const OWN = 'own' as const

export interface OwnOverlapViewProps {
  /** GET /projects, already held by the screen. Names, document counts and
   * stages all come from here — never a second fetch, and never a Cognito
   * lookup: at own scope no other person is ever named. */
  projects: Project[]
}

export default function OwnOverlapView({ projects }: OwnOverlapViewProps) {
  const [showWeak, setShowWeak] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()

  const owned = useMemo(() => projects.filter((p) => !p.shared_with_me), [projects])
  const ownedById = useMemo(() => new Map(owned.map((p) => [p.project_id, p])), [owned])
  const ownedIds = useMemo(() => owned.map((p) => p.project_id), [owned])
  const names = useMemo(() => new Map(projects.map((p) => [p.project_id, p.name])), [projects])

  const overlapQuery = useQuery({
    queryKey: ['portfolio-overlap', 'own'],
    queryFn: () => getPortfolioOverlap(null, OWN),
    retry: false,
    // Poll only while a run is actually in flight, never on a settled result.
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3000 : false),
  })
  const mapQuery = useQuery({
    queryKey: ['portfolio-map', 'own'],
    queryFn: () => getPortfolioMap(null, OWN),
    retry: false,
  })
  const fundingQuery = useQuery({
    queryKey: ['portfolio-overlap-funding', 'own'],
    queryFn: () => getPortfolioOverlapFunding(null, OWN),
    retry: false,
  })
  const agreementsQuery = useQuery({
    queryKey: ['disclosure-agreements'],
    queryFn: () => getDisclosureAgreements(),
    retry: false,
  })

  const overlap = overlapQuery.data
  const groups = useMemo(() => overlap?.groups ?? [], [overlap])
  const weakPairs = overlap?.weak_pairs ?? []

  // Deterministic banding — the SAME thresholds the aggregator view uses.
  // Groups form at 40 and above; 25-39 are weak signals and never form a group,
  // which is why they arrive as pairs rather than groups.
  const sameWork = useMemo(() => groups.filter((g) => overlapTierForScore(g.score) === 'strong'), [groups])
  const worthALook = useMemo(() => groups.filter((g) => overlapTierForScore(g.score) === 'moderate'), [groups])

  // The figure opens on the strongest group. Chosen from the REAL payload and
  // re-derived whenever it changes, but never overriding a group the reader has
  // since picked themselves.
  const strongestGroup = useMemo(
    () => (groups.length === 0 ? null : groups.reduce((a, b) => (b.score > a.score ? b : a))),
    [groups],
  )
  useEffect(() => {
    setSelectedGroupId((current) =>
      current && groups.some((g) => g.group_id === current) ? current : strongestGroup?.group_id ?? null,
    )
  }, [groups, strongestGroup])
  const selectedGroup = groups.find((g) => g.group_id === selectedGroupId) ?? null

  const noteQuery = useQuery({
    queryKey: ['overlap-group-note', 'own', selectedGroup?.group_id],
    queryFn: () => getOverlapGroupNote(selectedGroup?.group_id ?? '', null, OWN),
    enabled: Boolean(selectedGroup?.group_id),
    retry: false,
  })

  const rerun = useMutation({
    mutationFn: () => computePortfolioOverlap(null, OWN),
    onSuccess: (run) => {
      setNotice(
        run.cooldown_remaining_seconds
          ? `Another run is not available for ${Math.ceil(run.cooldown_remaining_seconds / 60)} more minutes — the last one is still the newest reading of the same documents.`
          : 'A fresh scan is running. The groups below update when it finishes.',
      )
      void queryClient.invalidateQueries({ queryKey: ['portfolio-overlap', 'own'] })
    },
    onError: (e) => setNotice((e as Error)?.message ?? 'The re-run could not be started.'),
  })

  const lifecycle = useMutation({
    mutationFn: ({ groupId, status }: { groupId: string; status: OverlapGroupStatusTarget }) =>
      setOverlapGroupStatus(groupId, status, null, OWN),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portfolio-overlap', 'own'] })
    },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      setNotice(status === 404 ? OVERLAP_STALE_GROUP_MESSAGE : (e as Error)?.message ?? 'That step could not be recorded.')
    },
  })

  const brief = useMutation({
    mutationFn: (groupId: string) => getOverlapGroupBrief(groupId, undefined, null, OWN),
    onSuccess: (b) => {
      downloadBase64File(b.docx_base64, b.filename, b.content_type)
      setNotice(`Downloaded ${b.filename}.`)
    },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      setNotice(status === 404 ? OVERLAP_STALE_GROUP_MESSAGE : (e as Error)?.message ?? 'The funding case could not be drafted.')
    },
  })

  const namingUnavailableReason = noteQuery.data?.available === false ? noteQuery.data.copy : undefined

  const comparedCaption = comparedCoverageLine({
    comparedCount: overlap?.compared_project_count,
    memberCount: overlap?.member_project_count,
    ownedCount: owned.length,
    sharedWithYouCount: projects.length - owned.length,
  })

  return (
    <div data-part="OwnOverlapView" className="grid items-start gap-3.5 lg:grid-cols-[318px_1fr]">
      {/* LEFT RAIL — the bands. */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[10.5px] font-extrabold uppercase tracking-wider text-gray-500">{comparedCaption}</p>
          <button
            type="button"
            onClick={() => rerun.mutate()}
            disabled={rerun.isPending}
            title="Re-run available after the cooldown, or the moment a document changes"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {rerun.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-run
          </button>
        </div>

        {overlapQuery.isLoading ? (
          <LoadingSkeleton type="card" rows={3} />
        ) : overlapQuery.isError ? (
          <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
            We could not load your overlap scan, so there is nothing to group. Nothing here is
            hidden from you — the number simply has not come back.
          </p>
        ) : (
          <>
            <BandLabel>{`${BAND_SAME_WORK} (${sameWork.length})`}</BandLabel>
            {sameWork.length > 0 ? (
              sameWork.map((g) => (
                <GroupCard
                  key={g.group_id}
                  group={g}
                  names={names}
                  selected={g.group_id === selectedGroupId}
                  onSelect={() => setSelectedGroupId(g.group_id)}
                />
              ))
            ) : (
              <EmptyBand>
                No two of your inventions scored 60 or above — none of them is substantially the same
                work as another.
              </EmptyBand>
            )}

            <BandLabel className="mt-3">{`${BAND_WORTH_A_LOOK} (${worthALook.length})`}</BandLabel>
            {worthALook.length > 0 ? (
              worthALook.map((g) => (
                <GroupCard
                  key={g.group_id}
                  group={g}
                  names={names}
                  selected={g.group_id === selectedGroupId}
                  onSelect={() => setSelectedGroupId(g.group_id)}
                />
              ))
            ) : (
              <EmptyBand>
                No group formed in the 40–59 band — no two of your inventions share enough ground to
                group them.
              </EmptyBand>
            )}

            <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3.5 py-3">
              <p className="text-[13px] font-bold text-gray-500">
                {BAND_WEAK} <span className="text-[11.5px] font-semibold">· score 25–39</span>
              </p>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-gray-500">
                Shown only when you choose to look, and never form a group.
              </p>
              {/* The control is ALWAYS on the screen, as the figure has it —
                  disabled with its own count and the reason when the scan found
                  no weak pair, so a reader can see the affordance exists and
                  why it is doing nothing (review round 1, item 4). */}
              <button
                type="button"
                disabled={weakPairs.length === 0}
                onClick={() => setShowWeak((v) => !v)}
                aria-expanded={weakPairs.length > 0 ? showWeak : undefined}
                title={
                  weakPairs.length === 0
                    ? 'No pair of yours scored in this band, so there is nothing to show'
                    : undefined
                }
                className="mt-2 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {showWeak && weakPairs.length > 0 ? 'Hide weak signals' : 'Show weak signals'} ({weakPairs.length})
              </button>
              {weakPairs.length === 0 ? (
                <p className="mt-1.5 text-[11.5px] text-gray-500">No pair of yours scored in this band.</p>
              ) : !showWeak ? null : (
                <ul className="mt-2 space-y-1">
                  {weakPairs.map((p) => (
                    <li key={`${p.project_a_id}-${p.project_b_id}`} className="text-[11.5px] text-gray-600">
                      {names.get(p.project_a_id) ?? p.project_a_id} ·{' '}
                      {names.get(p.project_b_id) ?? p.project_b_id} — {p.coefficient}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <div className="mt-3">
          <OwnFundingCard rollup={fundingQuery.data} callerUserId={user?.id} />
        </div>

        <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
          Groups form at score 40 and above ·{' '}
          <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
        </p>
      </div>

      {/* RIGHT — the open group. */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {overlapQuery.isLoading ? (
          <LoadingSkeleton type="card" rows={5} />
        ) : selectedGroup === null ? (
          <p className="text-[12.5px] text-gray-600">
            {overlapQuery.isError
              ? 'There is no group to open: your overlap scan did not come back.'
              : overlap?.status === 'processing'
                ? 'Your overlap scan is running. Groups appear here the moment it finishes — nothing is computed in this page.'
                : 'No group has formed across your inventions, so there is nothing to open here.'}
          </p>
        ) : (
          <div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-bold text-gray-900">
                  {overlapRelationshipHeadline(selectedGroup.score, selectedGroup.member_project_ids.length)}
                  <LifecyclePill status={selectedGroup.lifecycle_status} />
                </h3>
                <p className="mt-0.5 text-[12px] text-gray-500">{ownGroupMetaLine(selectedGroup)}</p>
              </div>
              <button
                type="button"
                disabled
                title={namingUnavailableReason ?? 'AI naming needs an organisation key registered for this portfolio'}
                className="shrink-0 cursor-not-allowed rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-400"
              >
                ✨ Name this group
              </button>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-gray-500">Lifecycle</p>
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  aria-current={(selectedGroup.lifecycle_status ?? 'new') === 'new' ? 'true' : undefined}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    (selectedGroup.lifecycle_status ?? 'new') === 'new'
                      ? 'border border-iflow-500 bg-iflow-50 text-iflow-800'
                      : 'border border-gray-200 text-gray-500'
                  }`}
                >
                  New
                </span>
                {LIFECYCLE_STEPS.map((step) => {
                  const current = selectedGroup.lifecycle_status === step
                  return (
                    <button
                      key={step}
                      type="button"
                      disabled={lifecycle.isPending}
                      aria-pressed={current}
                      onClick={() => lifecycle.mutate({ groupId: selectedGroup.group_id, status: step })}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                        current
                          ? 'border border-iflow-500 bg-iflow-50 text-iflow-800'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {LIFECYCLE_LABEL[step]}
                    </button>
                  )
                })}
                <button
                  type="button"
                  disabled
                  title={namingUnavailableReason ?? 'Notes need an organisation key registered for this portfolio'}
                  className="cursor-not-allowed rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-gray-400"
                >
                  ✎ Note
                </button>
              </div>
            </div>
            <p className="mt-1 text-[11.5px] text-gray-500">
              Every step is yours to take — you own both inventions, so no floor applies.
            </p>

            <div className="mt-3.5 grid items-start gap-3.5 lg:grid-cols-[1fr_212px]">
              <div>
                <p className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-gray-500">Members</p>
                <ul className="divide-y divide-gray-100">
                  {selectedGroup.member_project_ids.map((id) => {
                    const project = ownedById.get(id)
                    const stage = project ? getProjectStagePill(project, agreementsQuery.data) : null
                    return (
                      <li key={id} className="flex flex-wrap items-center gap-2 py-2 text-[12px]">
                        <span
                          aria-hidden
                          className="h-3 w-3 shrink-0 rounded"
                          style={{ background: projectTagColor(id, selectedGroup.member_project_ids) }}
                        />
                        <span className="min-w-0 flex-1 truncate font-semibold text-gray-900">
                          {names.get(id) ?? id}
                        </span>
                        {stage ? (
                          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-semibold text-gray-700">
                            {stage.label}
                          </span>
                        ) : (
                          <span className="shrink-0 text-[10.5px] text-gray-500">stage not recorded</span>
                        )}
                        <span className="shrink-0 text-[11px] text-gray-500">
                          {project ? `${project.document_count} docs` : 'document count unavailable'}
                        </span>
                        <button
                          type="button"
                          onClick={() => navigate(`/project/${id}`)}
                          className="shrink-0 text-[11.5px] font-semibold text-iflow-600 hover:text-iflow-800"
                        >
                          Open ▸
                        </button>
                      </li>
                    )
                  })}
                </ul>

                <p className="mb-1.5 mt-3.5 text-[10.5px] font-extrabold uppercase tracking-wider text-gray-500">
                  Evidence
                </p>
                <div className="flex items-start gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3.5 py-3 text-[12px] text-gray-500">
                  <span aria-hidden>ℹ</span>
                  <p>
                    <b className="text-gray-700">Document-pair evidence isn&apos;t available yet.</b> The
                    pairs behind this score are kept by the scan but not yet served here — the number
                    above is the whole of what the scan says. Open either invention to read its
                    documents.
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-gray-500">
                  Overlap mini-map
                </p>
                <OwnOverlapMiniMap
                  layout={mapQuery.data?.layout ?? null}
                  memberProjectIds={selectedGroup.member_project_ids}
                  projectNames={names}
                  score={selectedGroup.score}
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                  Amber is the shared ground. Only your own inventions are drawn.
                </p>
              </div>
            </div>

            {/* The fund-once workbench. */}
            <div className="mt-4 flex flex-wrap items-center gap-2.5 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-3.5 py-3">
              <p className="basis-full text-[13px] font-bold text-gray-900">
                Fund once, not {selectedGroup.member_project_ids.length} times:
              </p>
              <button
                type="button"
                onClick={() => brief.mutate(selectedGroup.group_id)}
                disabled={brief.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-2.5 py-1.5 text-[12px] font-bold text-gray-900 hover:bg-amber-300 disabled:opacity-50"
              >
                {brief.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                {brief.isPending ? 'Drafting…' : 'Draft consolidated funding case (.docx)'}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/ecosystem?type=funding_body&from=overlap-group-${selectedGroup.group_id}`)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
              >
                <Search size={14} /> Find funding bodies
              </button>
              {/* The figure's workbench is a title, two actions and the lead
                  line — nothing else. The consolidated-ask field the aggregator
                  workbench offers is not in it, and the brief route takes the
                  ask as optional, so the button works without one. What the
                  funding scan found is already stated in the left rail's card. */}
              <OwnNotifyBar
                memberProjectIds={selectedGroup.member_project_ids}
                ownedProjectIds={ownedIds}
              />
            </div>
          </div>
        )}

        {notice && (
          <p role="status" className="mt-2.5 text-[11px] text-gray-600">
            {notice}
          </p>
        )}
      </div>
    </div>
  )
}

function BandLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`mb-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-gray-500 ${className}`}>
      {children}
    </p>
  )
}

function EmptyBand({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-[11.5px] leading-relaxed text-gray-500">
      {children}
    </p>
  )
}

function LifecyclePill({ status }: { status?: string }) {
  const label = status === undefined || status === 'new' ? 'New' : LIFECYCLE_LABEL[status as OverlapGroupStatusTarget] ?? status
  const isNew = label === 'New'
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
        isNew ? 'bg-iflow-100 text-iflow-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}
    </span>
  )
}

function GroupCard({
  group,
  names,
  selected,
  onSelect,
}: {
  group: OverlapGroup
  names: Map<string, string>
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-part="OwnOverlapGroupCard"
      className={`mb-2.5 block w-full rounded-xl border bg-white px-3.5 py-3 text-left transition-colors ${
        selected ? 'border-iflow-500 ring-2 ring-iflow-100' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[13px] font-bold text-gray-900">
          {overlapRelationshipHeadline(group.score, group.member_project_ids.length)}
        </span>
        <LifecyclePill status={group.lifecycle_status} />
      </span>
      <span className="mt-1 block text-[11.5px] leading-relaxed text-gray-500">
        {group.member_project_ids.length}{' '}
        {group.member_project_ids.length === 1 ? 'invention' : 'inventions'} ·{' '}
        {group.member_project_ids.map((id, i) => (
          <span key={id}>
            {i > 0 && ' · '}
            <span
              aria-hidden
              className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-[-1px]"
              style={{ background: projectTagColor(id, group.member_project_ids) }}
            />
            {names.get(id) ?? id}
          </span>
        ))}
      </span>
      <span className="mt-1.5 block text-[11.5px] leading-relaxed text-gray-700">
        <b className="text-[13px] text-gray-900">Strongest pair: {group.score}</b> — the share of the
        smaller invention&apos;s documents with a match in the other
      </span>
    </button>
  )
}
